import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import InstructorCard from "../../components/cards/InstructorCard";
import OwlCarousel from "../../components/carousel";
import Footer from "../../components/footer";
import Header from "../../components/header";
import Loader from "../../components/loader";
import { useTranslate } from "../../context/TranslationContext";
import {
  useGetCategoriesQuery,
  useGetCreatorsByCategoryQuery,
} from "../../redux/Apis/Category";
import { useGetContentQuery } from "../../redux/Apis/General";
import { setMetaTags } from "../../utils/helper";

const CategoryInstructors = ({
  categoryId,
  categoryName,
  skipEmpty = false,
}) => {
  const navigate = useNavigate();
  const lang = useSelector((state) => state.language.lang);
  const { t } = useTranslate("instructorList");
  const { data, isLoading } = useGetCreatorsByCategoryQuery(
    {
      page: 1,
      rowsPerPage: 20,
      categoryId,
      lang,
    },
    { refetchOnMountOrArgChange: true },
  );

  const instructors = data?.data || [];

  if (isLoading) return <Loader loading />;

  if (!instructors.length && !skipEmpty)
    return (
      <div className="text-center text-muted py-5">
        <h5>
          {t("notFound")}
          {categoryName ? ` ${categoryName}` : ""}.
        </h5>
      </div>
    );

  if (!instructors.length) return null;

  return (
    <section className="mb-5">
      <div className="row mb-1">
        <div className="col-12 d-flex align-items-end gap-2 flex-wrap">
          <h4 className="text-uppercase mb-1">{categoryName}</h4>
          <p className="mb-0 m-purple-text">
            {instructors.length}{" "}
            {instructors.length > 1 ? t("instructors") : t("instructor")}
          </p>
        </div>
      </div>

      <div className="row">
        <OwlCarousel
          className="category-carousel"
          options={{
            margin: 10,
            loop: false,
            nav: true,
            dots: false,
            lazyLoad: true,
            autoplay: true,
            autoplayTimeout: 5000,
            autoplayHoverPause: true,
            smartSpeed: 500,
            navText: [
              '<i class="fas fa-angle-left"></i>',
              '<i class="fas fa-angle-right"></i>',
            ],
            responsive: {
              0: { items: 2, stagePadding: 0, autoplay: false },
              576: { items: 2, stagePadding: 20, autoplay: true },
              768: { items: 3, stagePadding: 0, autoplay: true },
              992: { items: 4, stagePadding: 0, autoplay: true },
              1200: { items: 5, stagePadding: 0, autoplay: true },
            },
          }}
        >
          {instructors.map((inst) => (
            <div
              key={inst._id}
              onClick={() => navigate(`/instructor/${inst._id}`)}
            >
              <InstructorCard data={inst} />
            </div>
          ))}
        </OwlCarousel>
      </div>
    </section>
  );
};

const Users = () => {
  const { t } = useTranslate("instructorList");
  const lang = useSelector((state) => state.language.lang);
  const [selectedCategory, setSelectedCategory] = useState("");
  const { data: categoryData, isLoading: isCategoryLoading } =
    useGetCategoriesQuery({ lang }, { refetchOnMountOrArgChange: true });
  const categories = categoryData?.data || [];

  const [isMobile, setIsMobile] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const { data: featuredVideos, isLoading: isFeaturedVideosLoading } =
    useGetContentQuery({
      search: "featured-videos",
      lang,
    });

  const templateVideoUrl =
    featuredVideos?.data?.sections?.find(
      (s) => s.title === "Featured Creator Video",
    )?.content || "";

  const getEmbedUrl = (url, autoplay = true) => {
    if (!url) return null;

    try {
      const urlObj = new URL(url);

      if (
        !urlObj.hostname.includes("youtube.com") &&
        !urlObj.hostname.includes("youtu.be")
      ) {
        return null;
      }

      const videoId = urlObj.searchParams.get("v");
      const listId = urlObj.searchParams.get("list");

      if (listId) {
        return `https://www.youtube.com/embed/videoseries?list=${listId}${
          autoplay ? "&autoplay=1&mute=1" : ""
        }`;
      }

      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}${
          autoplay ? "?autoplay=1&mute=1" : ""
        }`;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const embedUrl = getEmbedUrl(templateVideoUrl, !isMobile);

  useEffect(() => {
    setMetaTags(
      "Traineego | Buy or Create Automated Courses & Tracks for Fitness & Wellness",
      "Create, manage, and sell your own courses and tracks, or buy high-quality content. Empower your career through automation and passive income today!",
    );
  }, []);

  if (isCategoryLoading || isFeaturedVideosLoading)
    return (
      <>
        <Header />
        <Loader loading />
      </>
    );
  const scrollToTop = () => {
    window.scrollTo({ behavior: "smooth", top: 0 });
  };

  return (
    <>
      <Header />
      <section style={{ minHeight: "100vh", padding: "120px 0" }}>
        {embedUrl && (
          <div className="container mb-5">
            <div className="video-container ratio ratio-16x9 mb-5">
              <iframe
                src={embedUrl}
                title="Featured Template Video"
                allow="autoplay; encrypted-media"
                allowFullScreen
                style={{ borderRadius: "12px" }}
              />
            </div>
          </div>
        )}

        <div className="sticky-header-wrapper mb-5">
          <div className="container my-2">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
              <p
                className="text-white fw-bold mb-0"
                style={{ fontSize: "32px" }}
              >
                {t("title")}
              </p>

              <div style={{ width: "100%", maxWidth: "280px" }}>
                <Select
                  isClearable
                  placeholder={t("selectCat")}
                  value={
                    selectedCategory
                      ? {
                          value: selectedCategory,
                          label:
                            categories.find((c) => c._id === selectedCategory)
                              ?.name || "Unknown",
                        }
                      : null
                  }
                  onChange={(option) =>
                    setSelectedCategory(option ? option.value : "")
                  }
                  options={categories.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: "6px",
                      minHeight: "38px",
                      fontSize: "14px",
                    }),
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                    option: (base, { isFocused, isSelected }) => ({
                      ...base,
                      backgroundColor: isSelected
                        ? "var(--primaryColor)"
                        : isFocused
                          ? "#e9ecef"
                          : "#f8f9fa",
                      color: isSelected ? "#fff" : "#000",
                      cursor: "pointer",
                      ":active": {
                        ...base[":active"],
                        backgroundColor: "#0056b3",
                      },
                    }),
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="container">
          {selectedCategory ? (
            <CategoryInstructors
              categoryId={selectedCategory}
              categoryName={
                categories.find((c) => c._id === selectedCategory)?.name
              }
            />
          ) : (
            categories
              .filter((cat) => cat._id)
              .map((cat) => (
                <CategoryInstructors
                  key={cat._id}
                  categoryId={cat._id}
                  categoryName={cat.name}
                  skipEmpty
                />
              ))
          )}
        </div>
      </section>
      <Footer />

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="btn purple-text rounded-circle shadow"
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 1050,
            width: "50px",
            height: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i className="bi bi-arrow-up-circle-fill fs-3"></i>
        </button>
      )}
    </>
  );
};

export default Users;
